import type { SidebarSchema } from "./sidebarSchema.ts";

/**
 * Per-sidebar-type routing config. Today only one knob:
 * `mobilePageRoute` — when present and the viewport is mobile, `openSidebar`
 * will navigate to this URL instead of rendering the Sheet overlay. Used for
 * sidebars whose mobile experience is better served as a full-screen page
 * (e.g. a communication, which reads better as a full page than a Sheet).
 *
 * Only imperative `openSidebar` callers reach this table — a link is already a
 * canonical URL, and `SidebarNavBoundary` simply declines it on mobile so it
 * loads the real page.
 *
 * Per-type entries are typed so each builder receives its specific schema
 * variant (not the un-narrowed union), making it a compile error to access
 * fields that don't exist on that variant.
 */
type SidebarRouting = {
  [T in SidebarSchema["type"]]?: {
    mobilePageRoute?: (state: Extract<SidebarSchema, { type: T }>) => string;
  };
};

const sidebarRouting: SidebarRouting = {
  // On mobile, open the communication as its own full-screen page rather than a
  // Sheet overlay (better for reading a full email).
  communication: {
    mobilePageRoute: (s) => `/communications/${s.id}`,
  },
};

/**
 * Resolve a sidebar state to a page URL on mobile, if the type has registered
 * a `mobilePageRoute`. Returns null for types that stay as Sheet overlays on
 * mobile (current behavior for every non-calendar type). Callers use the
 * returned URL to `setLocation` directly instead of writing `sb_*` params.
 */
export function getMobilePageRoute(state: SidebarSchema): string | null {
  const entry = sidebarRouting[state.type];
  if (!entry?.mobilePageRoute) return null;
  // The registry's typed mapped-type enforces per-variant correctness at the
  // definition site; at the call site we lose that narrowing and have to cast
  // across the union. Safe: `state.type` just selected the matching entry.
  const fn = entry.mobilePageRoute as (s: SidebarSchema) => string;
  return fn(state);
}
